<template>
  <div class="create-client-note">
    <div v-if="selectedClientNote" class="create-client-note__title">Edit Project Note</div>
    <div v-else class="create-client-note__title">Create New Project Note</div>
    <label class="create-client-note__input">
      <span class="create-client-note__label">Category</span>
      <div class="create-client-note__link-wrapper"
           :class="{'create-client-note__link-wrapper_single': isDomainDropdownHidden}">
        <base-select
          border-less
          alignLeft
          class="create-client-note__category"
          :class="{'form-control_error': externalErrors.category_id}"
          :options="opts.categories"
          v-model="clientNote.category_id"
          :max-height-list="450"
          :titleStyle="titleStyle"
          :headerStyle="selectTypeStyle"
        />
        <span class="create-client-note__error-message"
              v-if="externalErrors.category_id">{{externalErrors.category_id}}</span>
      </div>
    </label>
    <label class="create-client-note__input">
      <span class="create-client-note__label">Title</span>
      <div class="create-client-note__link-wrapper"
           :class="{'create-client-note__link-wrapper_single': isDomainDropdownHidden}">
        <input type="text"
               class="form-control create-client-note__link-field"
               placeholder="Enter the Client Note Title"
               :class="{'form-control_error': externalErrors.name}"
               v-model="clientNote.name">
        <span class="create-client-note__error-message" v-if="externalErrors.name">{{externalErrors.name}}</span>
      </div>
    </label>
    <label class="create-client-note__input">
      <span class="create-client-note__label">Content</span>
      <editor
        v-model="clientNote.content"
        :disabled="isCurrentUserClientCollaborator"
        :init="{
            verify_html: false,
            menubar: false,
            statusbar: false,
            plugins: 'advlist autolink lists link image charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table paste code help wordcount',
            toolbar: 'undo redo | formatselect | bold italic backcolor | link | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
            height: '300'
          }"
      ></editor>
    </label>
    <div class="create-client-note__controls">
      <button class="create-client-note__button create-client-note__button_cancel"
              @click.prevent="close()">Cancel
      </button>
      <button v-if="selectedClientNote" class="create-client-note__button create-client-note__button_delete"
              @click.prevent="openConfirmModal()">
        Delete
      </button>
      <button class="create-client-note__button create-client-note__button_submit"
              @click.prevent="apply()">
        Apply
      </button>
    </div>
    <modal name="confirm-modal" v-bind="modalProps">
      <base-confirm-modal title="Are you sure you want to delete this?"
                          subtitle="This Project Note will be removed permanently!"
                          @confirm="confirmModal" />
    </modal>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import Filters from '@/common/Filters'
import _ from 'lodash'
import baseModalProps from '@/common/BaseModalProps'
import BaseModalMixins from '@/common/BaseModalMixins'

const SELECT_STYLE = {
  'background-color': 'transparent',
  color: '#284aff',
  'text-decoration': 'underline'
}
const OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
export default {
  name: 'CreateUpdateClientNotePopup',
  mixins: [BaseModalMixins],
  components: {
    editor: Editor
  },
  props: {
    selectedClientNote: {
      type: Object
    },
    opts: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      clientNote: _.cloneDeep(this.selectedClientNote) || {
        name: '',
        category_id: -1,
        category: '',
        created: '',
        updated: '',
        content: ''
      },
      activeOpts: [...OPTIONS],
      selectTypeStyle: { ...SELECT_STYLE },
      externalErrors: {
        name: null,
        category_id: null
      },
      isValidating: false,
      // should domain name selector be hidden or not
      isDomainDropdownHidden: true,
      titleStyle: {
        overflow: 'visible'
      }
    }
  },
  computed: {
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    }
  },
  methods: {
    showErrors (errors) {
      for (const k in this.externalErrors) {
        this.externalErrors[k] = null
      }

      for (const k in errors) {
        this.externalErrors[k] = errors[k]
      }
    },
    close () {
      this.$emit('cancel')
    },
    openConfirmModal () {
      this.$modal.show('confirm-modal')
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.deleteClientNote()
      }
    },
    apply () {
      this.$emit('apply', this.clientNote)
    },
    deleteClientNote () {
      this.$emit('delete', this.clientNote.id)
    }
  },
  filters: {
    ...Filters
  }
}
</script>

<style lang="scss">
.create-client-note {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 40px;

  &__title {
    text-align: center;
    font-size: 34px;
    font-weight: 500;
    margin: 9px 0 35px;
    color: $rfx-dark-march;
  }

  &__input {
    display: flex;
    flex-direction: column;
    position: relative;
    margin-top: 30px;

    &_invalid[type='text'] {
      border: 1px solid $rfx-red;
      border-radius: 4px;
    }
  }

  &__label {
    display: block;
    font-weight: 600;
    margin-bottom: 5px;
    color: $rfx-dark-march;
  }

  &__controls {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
  }

  &__button {
    width: 100px;
    height: 40px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    margin-left: 30px;
    border: none;
    outline: none;
    cursor: pointer;

    &_submit {
      background-color: $rfx-medium-march;
      color: #fff;

      &:disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }
    }

    &_delete {
      background-color: $rfx-red;
      color: #fff
    }

    &_cancel {
      color: $rfx-dark-march;
      background-color: #fff;
    }
  }

  &__link {
    &-wrapper {
      position: relative;
      margin-top: 30px;

      &_single {
        margin-top: 0;
      }
    }

    &-field[type='text'] {
      width: 100%;
      box-sizing: border-box;
      padding-right: 60px;
      font-weight: 500;
      @include ellipsis;
    }
  }

  &__error-message {
    color: $rfx-red;
    font-size: 10px;
    margin-top: 5px;
  }
}

.form-control {
  height: 50px;
  padding: 0 20px;
  border: 2px solid $rfx-light-steel;
  outline: none;
  border-radius: $rfx-default-border-radius;
  font-size: 14px;
  font-weight: 600;
  color: $rfx-dark-march;

  &::placeholder {
    color: $rfx-pearl;
    font-weight: normal;
    font-size: 14px;
  }

  &:disabled {
    background-color: $rfx-light-steel;
  }

  &_error {
    border-color: $rfx-red;
    color: $rfx-red;

    &::placeholder {
      color: $rfx-red;
      font-size: 13px;
    }

  }
}

.description {
  width: 100%;
  height: 90%;
  padding: 0;
  resize: none;
  outline: none;
  border: none;
  font-size: 14px;

  &::placeholder {
    color: $rfx-pearl;
    font-weight: normal;
    font-size: 14px;
    margin-top: -5px;
  }

  &__wrapper {
    padding: 10px 20px;
    height: auto;
  }
}

</style>
